import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  GraduationCap,
  Users,
  BookOpen,
  IdentificationCard,
  EnvelopeSimple,
  CreditCard,
  Translate,
  Sparkle,
} from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Countdown from "../components/Countdown";
import WhatsAppFab from "../components/WhatsAppFab";

const CARDS = [
  {
    to: "/course",
    eyebrow: "01 · The Program",
    title: "Decoding Market Profile & Order Flow",
    desc: "The 6-month flagship course. Syllabus, mentor, interactive TPO preview, packages and FAQ.",
    icon: GraduationCap,
    accent: "#D9381E",
    testId: "home-card-course",
    cta: "Explore the course",
  },
  {
    to: "/packages",
    eyebrow: "02 · Pricing",
    title: "Starter · Core · Pro",
    desc: "₹20,000 to ₹40,000. Three tiers — plus an optional 1-year upgrade to Pro at ₹50,000 with 12 mentor calls.",
    icon: CreditCard,
    accent: "#FFB703",
    testId: "home-card-packages",
    cta: "See packages",
  },
  {
    to: "/community",
    eyebrow: "03 · Live Community",
    title: "₹999 · 10 trading days of live trading",
    desc: "₹99 / trading day. Daily trade plan, Nifty / Bank Nifty / Midcap levels, live EOD discussion — a quick intro to our trading ecosystem.",
    icon: Users,
    accent: "#25D366",
    testId: "home-card-community",
    cta: "Join the community",
  },
  {
    to: "/resources",
    eyebrow: "04 · Resources",
    title: "GoCharting · Brokers · Videos",
    desc: "Recommended trading account links, our YouTube session highlights and GoCharting partner access.",
    icon: BookOpen,
    accent: "#3B5BDB",
    testId: "home-card-resources",
    cta: "Browse resources",
  },
  {
    to: "/mentor",
    eyebrow: "05 · The Mentor",
    title: "Mr. Vishnu Gupthan R.",
    desc: "Working professional trader. Panelist, South India · GoCharting. 20+ years market experience · 500+ students trained.",
    icon: IdentificationCard,
    accent: "#0A0A0A",
    testId: "home-card-mentor",
    cta: "Meet the mentor",
  },
  {
    to: "/contact",
    eyebrow: "06 · Talk to us",
    title: "Trivandrum · Bengaluru",
    desc: "WhatsApp, phone, email or drop us a note. We reply within 24 hours, every working day.",
    icon: EnvelopeSimple,
    accent: "#D9381E",
    testId: "home-card-contact",
    cta: "Get in touch",
  },
  {
    to: "/batches",
    eyebrow: "07 · New Batches",
    title: "English batch · Offline batch",
    desc: "By popular demand — a 100% English online batch starts 15 Aug 2026. Plus a 15-day classroom immersion (max 8 seats).",
    icon: Translate,
    accent: "#0A0A0A",
    testId: "home-card-batches",
    cta: "See new batches",
    highlight: true,
  },
  {
    to: "/decide",
    eyebrow: "08 · Try before you commit",
    title: "Not sure yet? Experience us first.",
    desc: "Join the ₹999 live plan, speak to alumni, request a community trial or get 3-day demo access to the library. Then decide.",
    icon: Sparkle,
    accent: "#FFB703",
    testId: "home-card-decide",
    cta: "Try before you commit",
    highlight: true,
  },
];

export default function Home() {
  return (
    <div
      data-testid="home-root"
      className="min-h-screen bg-[#F4F3ED] text-[#0A0A0A] font-body"
    >
      <Navbar />

      <main className="pt-36 lg:pt-40">
        {/* Hero */}
        <section className="px-5 lg:px-10">
          <div className="max-w-[1320px] mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#4A4A4A] border border-[#D4D0C5] px-2 py-1">
                Cohort 15 / 2026
              </span>
              <span className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E]">
                ● Enrolling Now
              </span>
            </div>

            <h1
              data-testid="home-headline"
              className="font-display font-black tracking-tighter text-[#0A0A0A] leading-[0.92] text-5xl sm:text-6xl lg:text-[88px]"
            >
              DECODE
              <br />
              THE <span className="text-[#D9381E]">AUCTION.</span>
              <br />
              TRADE THE
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">PARTICIPATION.</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#D9381E]/20 -z-0" />
              </span>
            </h1>

            <p className="font-body text-base md:text-lg text-[#4A4A4A] mt-7 max-w-2xl">
              An institutional-grade program on{" "}
              <span className="font-semibold text-[#0A0A0A]">
                Market Profile
              </span>{" "}
              and{" "}
              <span className="font-semibold text-[#0A0A0A]">Order Flow</span>{" "}
              for Indian intraday, swing, options and MCX traders. Cohort 15
              commences <strong>31 August 2026, 09:15 IST</strong>.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/course"
                data-testid="home-cta-course"
                className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-[#F4F3ED] px-7 py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors"
              >
                Explore the course
                <ArrowRight weight="bold" className="text-[#D9381E]" />
              </Link>
              <Link
                to="/community"
                data-testid="home-cta-community"
                className="inline-flex items-center justify-center gap-3 border border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
              >
                <span className="font-mono-acc font-bold">₹999</span>
                Join the live community
              </Link>
            </div>

            <div className="mt-10">
              <Countdown />
            </div>
          </div>
        </section>

        {/* Cards */}
        <section className="px-5 lg:px-10 py-20 lg:py-28">
          <div className="max-w-[1320px] mx-auto">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
                  WHERE TO GO
                </div>
                <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
                  Pick your path.
                </h2>
              </div>
              <p className="font-body text-[#4A4A4A] max-w-md">
                Each section has its own shareable link. Send the one that fits
                your conversation — full course, ₹999 live, or just the
                broker setup.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {CARDS.map((c) => {
                const Icon = c.icon;
                return (
                  <Link
                    key={c.to}
                    to={c.to}
                    data-testid={c.testId}
                    className="group border border-[#0A0A0A] bg-[#F4F3ED] p-7 flex flex-col hover:bg-[#0A0A0A] hover:text-[#F4F3ED] transition-colors"
                  >
                    <div className="flex items-center justify-between mb-7">
                      <span
                        className="font-mono-acc text-[10px] tracking-[0.25em] uppercase"
                        style={{ color: c.accent }}
                      >
                        {c.eyebrow}
                      </span>
                      <Icon
                        size={26}
                        weight="duotone"
                        style={{ color: c.accent }}
                      />
                    </div>
                    <h3 className="font-display font-black text-2xl lg:text-[26px] tracking-tighter leading-tight mb-3">
                      {c.title}
                    </h3>
                    <p className="font-body text-sm text-[#4A4A4A] group-hover:text-[#A1A1AA] leading-relaxed mb-7 flex-1">
                      {c.desc}
                    </p>
                    <div className="inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase border-b border-current pb-1 self-start">
                      {c.cta}
                      <ArrowRight
                        size={14}
                        weight="bold"
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
